import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Only } from 'ui/components/elements/Only/Only';
import IconTriangle from 'ui/images/top/icon_triangle.svg';
import IconArrow from 'ui/images/top/icon_slidearrow.svg';
import NoImage from 'ui/images/no_image.png';
import '@splidejs/react-splide/css';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import type { Splide as SplideInstance } from '@splidejs/splide';
import { mqMin } from 'utils/responsive';
import { useCases } from './useCases';
import { useNavigate } from 'react-router-dom';

const Container = styled.section`
    position: relative;
`;

const CasesWrapper = styled.div`
    background-color: var(--lightblue);

    ${mqMin('NARROW')} {
        background: linear-gradient(
            0deg,
            rgba(223, 236, 243, 1) 0%,
            rgba(223, 236, 243, 1) 69.9%,
            rgba(255, 255, 255, 1) 70%,
            rgba(255, 255, 255, 1) 100%
        );
    }
`;

const CasesContent = styled.div`
    padding: 50px 0;
    background-color: var(--lightblue);

    ${mqMin('NARROW')} {
        display: flex;
        width: 100%;
        margin: 0 auto 0 auto;
    }
`;

const TitleContainer = styled.div`
    h3 {
        text-align: center;
        font-size: 1.5rem; /*24px*/
        line-height: 1.8em;
        letter-spacing: 0.1em;
        margin-bottom: 20px;
    }

    ${mqMin('NARROW')} {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        align-content: center;
        width: 38.19444444444444%;
        margin-bottom: 0;
        padding-top: 170px;

        h3 {
            text-align: left;
            margin-bottom: 28px;
        }
    }
`;

const CasesCountContainer = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: center;
    text-align: center;
    font-size: 1.125rem; /*18px*/
    letter-spacing: 0.1em;
    margin-bottom: 49px;

    ${mqMin('NARROW')} {
        width: 100%;
    }
`;

const CasesNumberText = styled.div`
    padding-bottom: 7px;
    margin: 0 5px;
`;

const CasesNumber = styled.div`
    display: block;
    background-color: #fff;
    border-radius: 8px;
    font-size: 1.625rem; /*26px*/
    line-height: 1.8rem;
    letter-spacing: 0.1em;
    padding: 10px;
`;

const ListWrapper = styled(Splide)`
    display: none;

    ${mqMin('SP')} {
        display: block;
        margin: 0 auto 0 auto;
    }
    ${mqMin('NARROW')} {
        overflow: visible;
        position: relative;
        width: 61.80555555555556%;
        max-width: 910px;
    }
`;

const ListWrapperForSP = styled(Splide)`
    display: flex;
    flex-direction: column;

    ${mqMin('SP')} {
        width: 83.72093023255814%;
        margin: 0 auto 0 auto;
    }
    ${mqMin('NARROW')} {
        position: relative;
        width: 61.80555555555556%;
        max-width: 910px;
    }
`;

const Arrows = styled.div`
    display: none;
    ${mqMin('NARROW')} {
        display: block;
        width: 100%;
        position: relative;
        top: 50%;
        transform: translateY(-50%);
        z-index: 2;
    }
`;

const ArrowButton = styled.button`
    top: initial !important;
    ${mqMin('NARROW')} {
        width: 50px;
        height: 100px;
        background: none;
        padding: 0;
        transition: all 0.3s;
    }
`;

const ArrowPrev = styled(ArrowButton)`
    position: absolute;
    left: 0;
    transform: scale(-1, 1);
    z-index: 2;
`;

const ArrowNext = styled(ArrowButton)`
    position: absolute;
    right: 0;
    z-index: 2;
    transform: initial !important;
`;

const SlideCard = styled(SplideSlide)`
    width: 83.72093023255814%;
    max-width: 270px;
    border-radius: 12px;
    background-color: #fff;
    margin: 0 auto 28px auto;

    ${mqMin('NARROW')} {
        width: 270px;
        min-height: 400px;
        margin: initial;
    }
`;

const SlideImgWrapper = styled.div`
    overflow: hidden;
    width: 100%;
    position: relative;
    aspect-ratio: 72 / 31;
    border-radius: 12px 12px 0 0;
    height: 150px;
    img {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        border-radius: 12px 12px 0 0;
    }

    ${mqMin('SP')} {
        aspect-ratio: 9 / 5;
    }
`;

const AgentImg = styled.img`
    width: 100%;
    object-fit: cover;
    transition: all 0.4s;
    object-position: center center;

    &:hover {
        scale: 1.1;
    }
`;

const DescriptionContainer = styled.div`
    padding: 20px;

    h4 {
        position: relative;
        font-size: 1.25rem; /*20px*/
        line-height: 1.8rem;
        letter-spacing: 0.1em;

        &::after {
            content: '';
            display: block;
            width: 40px;
            height: 2px;
            margin: 14px 0;
            background-color: var(--dark-blue);
        }

        ${mqMin('SP')} {
            position: relative;
            font-size: 1rem; /*16px*/

            &::after {
                width: 28px;
                height: 2px;
                margin: 10px 0;
            }
        }

        ${mqMin('NARROW')} {
            position: relative;
            font-size: 1rem; /*16px*/

            &::after {
                width: 28px;
                height: 2px;
                margin: 10px 0;
            }
        }
    }
`;

const DescriptionText = styled.p`
    line-height: 1.6rem;
    letter-spacing: 0.1em;
    white-space: pre-line;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 10;
    -webkit-box-orient: vertical;

    ${mqMin('SP')} {
        margin: auto;
        font-size: 0.8125rem; /*13px*/
    }

    ${mqMin('NARROW')} {
        font-size: 0.8125rem; /*13px*/
    }
`;

const RegisterButtonContainer = styled(SplideSlide)`
    margin: auto;

    button {
        display: block;
        background-color: var(--orange);
        border-radius: 8px;
        border: solid 1px #fff;
        color: #fff;
        text-align: center;
        text-decoration: none;
        padding: 16px 20px;
        margin: 0 auto 0 auto;
        transition: all 0.3s;

        &:hover {
            color: var(--orange);
            background-color: #fff;
        }

        &:visited {
            color: #fff;
        }
    }
`;

export const Cases: React.FC = () => {
    const [splideInstance, setSplideInstance] = useState<SplideInstance | null>(null);
    const { agents, matchCount } = useCases();
    const navigate = useNavigate();
    const goToRegister = () => {
        navigate('/send-registration-mail/');
    };

    return (
        <Container>
            <CasesWrapper>
                <CasesContent>
                    <TitleContainer className="fadein fadein_y">
                        <h3>
                            MAnyが提携している
                            <br />
                            M&A仲介会社の例
                        </h3>
                        <CasesCountContainer>
                            <CasesNumberText>
                                <img src={IconTriangle} />
                            </CasesNumberText>
                            {/* {matchCount > 0 && (
                                <>
                                    <CasesNumberText>現在のマッチング数</CasesNumberText>
                                    <CasesNumber>{matchCount}</CasesNumber>
                                    <CasesNumberText>件</CasesNumberText>
                                </>
                            )} */}
                        </CasesCountContainer>
                    </TitleContainer>

                    <Only pc>
                        <Arrows>
                            <ArrowPrev onClick={() => splideInstance?.go('<')}>
                                <img src={IconArrow} />
                            </ArrowPrev>
                            <ArrowNext onClick={() => splideInstance?.go('>')}>
                                <img src={IconArrow} />
                            </ArrowNext>
                        </Arrows>

                        <ListWrapper
                            id="splide01"
                            className="splide fadein fadein_y"
                            role="group"
                            aria-label="事例リスト"
                            onMounted={(splide) => {
                                setSplideInstance(splide);
                            }}
                            options={{
                                perPage: 1,
                                autoWidth: true,
                                gap: '36px',
                                pagination: false,
                                hasArrows: true,
                                arrows: false,

                                breakpoints: {
                                    768: {
                                        perPage: 1,
                                    },
                                    1024: {
                                        perPage: 3,
                                    },
                                },
                            }}
                        >
                            {agents.map((agent) => (
                                <SlideCard key={agent.id}>
                                    <SlideImgWrapper>
                                        <AgentImg src={agent.icon_image || NoImage} alt={agent.name} />
                                    </SlideImgWrapper>
                                    <DescriptionContainer>
                                        <h4>{agent.name}</h4>
                                        <DescriptionText>{agent.business_description}</DescriptionText>
                                    </DescriptionContainer>
                                </SlideCard>
                            ))}
                            <RegisterButtonContainer
                                onClick={() => {
                                    goToRegister();
                                }}
                            >
                                <button>登録してもっと見る</button>
                            </RegisterButtonContainer>
                        </ListWrapper>
                    </Only>

                    <Only sp>
                        <ListWrapperForSP
                            className="fadein fadein_y"
                            aria-label="事例リスト"
                            options={{
                                perPage: 1,
                                autoWidth: true,
                                gap: '36px',
                                pagination: false,
                                hasArrows: true,
                                arrows: false,
                            }}
                        >
                            {agents.map((agent) => (
                                <SlideCard key={agent.id}>
                                    <SlideImgWrapper>
                                        <AgentImg src={agent.icon_image || NoImage} alt={agent.name} />
                                    </SlideImgWrapper>
                                    <DescriptionContainer>
                                        <h4>{agent.name}</h4>
                                        <DescriptionText>{agent.business_description}</DescriptionText>
                                    </DescriptionContainer>
                                </SlideCard>
                            ))}

                            <RegisterButtonContainer
                                onClick={() => {
                                    goToRegister();
                                }}
                            >
                                <button>登録してもっと見る</button>
                            </RegisterButtonContainer>
                        </ListWrapperForSP>
                    </Only>
                </CasesContent>
            </CasesWrapper>
        </Container>
    );
};
